import React, { useContext, ReactElement } from "react";
import { useParams } from "react-router-dom";
import EditIcon from "@material-ui/icons/Edit";
import ViewIcon from "@material-ui/icons/Visibility";
import DeleteIcon from "@material-ui/icons/Delete";
import CustomTable, { CustomTableRowData } from "../../../components/CustomTable";
import TestRequestApi from "../../../api/services/TestRequestApi";
import { SubstrateSampleContext } from "../../../contexts/SubstrateSampleContext";
import SubstrateSample from "../../../api/models/substrate/SubstrateSample";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { tesaDarkRedError } from "../../../styles/colors";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import formatIdentifierAsString from "../../../api/services/common/formatIdentifierAsString";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import useEnv from "../../../hooks/env";
import { WithPredefinedChip } from "../../../components/Chip/PredefinedSampleChip";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";

interface SubstrateTableProps {
  callbackUpdate: Function;
  onEdit: (sampleId: number) => void;
  substrateSamples: SubstrateSample[];
}

const SubstrateTable = (props: SubstrateTableProps): ReactElement => {
  const { callbackUpdate, onEdit: handleEditById, substrateSamples } = props;
  const { api } = useEnv();
  const testRequestApi: TestRequestApi = new TestRequestApi(api);
  const { testRequestId } = useParams<RequestsPagePathParams>();
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleDialogNegativeCallback, handleHttpError } = useContext(ErrorSnackbarContext);
  const { substrateSamples: substrateActions } = useContext(TestRequestPermissionContext);

  const showEdit = (data: CustomTableRowData): boolean => {
    const isReuse = !!data.usedIn;
    return (
      !isReuse &&
      (substrateActions?.[data.id]?.editDefinition ||
        substrateActions?.[data.id]?.editAttachments ||
        substrateActions?.[data.id]?.editCharacteristics ||
        false)
    );
  };
  const showViewDetails = (data: CustomTableRowData): boolean =>
    (!showEdit(data) && substrateActions?.[data.id]?.showDetails) || false;

  const handleEdit = (rowData: CustomTableRowData): void => {
    if (showEdit(rowData) || showViewDetails(rowData)) {
      handleEditById(rowData.id);
    }
  };

  const handleDelete = (id: number) => {
    testRequestApi
      .deleteTestRequestSubstrateSamples(testRequestId, [id])
      .then(() => {
        callbackUpdate();
      })
      .catch((error) => {
        if (error?.response?.data?.errorCode === "SUBSTRATE_SAMPLE_IN_USE") {
          showConfirmationDialog({
            headline: "Substrate sample in use",
            confirmText: "Okay",
            text: `This substrate sample is part of a started test series and can therefore not be 
            deleted.`,
            hideCancel: true,
          }).catch(() => {
            // ignore cancel
          });
        } else {
          handleHttpError(error);
        }
      });
    return id;
  };

  const onDelete = (entry: CustomTableRowData) => {
    showConfirmationDialog({
      headline: "Are you sure?",
      text: (
        <>
          You are about to delete <b>{entry.sampleName}</b>.
        </>
      ),
      confirmText: "Delete",
      confirmColor: tesaDarkRedError,
    })
      .then(() => handleDelete(entry.id))
      .catch(handleDialogNegativeCallback);
  };

  const generateRows = (): CustomTableRowData[] => {
    let tableRows: CustomTableRowData[] = [];
    if (substrateSamples) {
      tableRows = substrateSamples.map((substrateSample: SubstrateSample, index: number) => {
        return {
          sampleName: substrateSample.usedIn ? (
            <WithPredefinedChip text={substrateSample.sampleName} />
          ) : (
            substrateSample.sampleName
          ),
          substrateType: formatIdentifierAsString(substrateSample.substrateType),
          substrateClass: substrateSample.substrateClassLayers[0].substrateClassName,
          description: substrateSample.description,
          deliveredBy: formatIdentifierAsString(substrateSample.deliveredBy),
          usedIn: substrateSample.usedIn,

          id: substrateSample.id || 0,
          rowNumber: index + 1,
        };
      });
    }
    return tableRows;
  };

  return (
    <>
      <CustomTable
        actionColumnIndexes={[0]}
        selectedItemNameValue="sampleName"
        boldIndex={0}
        columns={["Substrate Sample Name", "Substrate Type", "Substrate Class", "Note", "Delivered By"]}
        columnMaxWidths={["16vw", "16vw", "16vw", "16vw", "16vw"]}
        excludedKeys={["id", "usedIn"]}
        formatDates
        rowMenuActions={[
          {
            action: (rowData): void => handleEditById(rowData.id),
            icon: <ViewIcon />,
            text: "View Details",
            show: showViewDetails,
          },
          {
            action: (rowData): void => handleEditById(rowData.id),
            icon: <EditIcon />,
            text: "Edit",
            show: showEdit,
          },
          {
            action: (rowData): void => onDelete(rowData),
            icon: <DeleteIcon />,
            text: "Delete",
            show: (data: { id: number }) => substrateActions?.[data.id]?.delete || false,
          },
        ]}
        rowMenus
        hasRowNumbers
        handleEdit={handleEdit}
        rows={generateRows()}
      />
    </>
  );
};

export default SubstrateTable;
